import { Fragment } from "react";
import { cn } from "@repo/utils";
import type { RowBase, ActionContext } from "./data-table.js";
import type { ColumnDef, RowClassNameType } from "./data-table.types.js";
import type { ItemProps } from "../dropdown-menu/dropdown-menu-item-list.js";
import { DropdownMenu } from "../dropdown-menu/dropdown-menu.js";
import { defaultGetRowId } from "./utils/data-table-utils.js";

type Props<TRow extends RowBase, TActionContext = ActionContext<TRow>> = {
  data: TRow[];
  columns: ColumnDef<TRow>[];
  actions?: ItemProps<TActionContext>[];
  bodyClassName?: string;
  rowClassName?: RowClassNameType<TRow>;
  onRowClick?: (row: TRow, index: number) => void;
  showActionsColumn: boolean;
  actionsColumnContent?: React.ReactNode;
  hasActions: boolean;
  hasSearchableColumns: boolean;
  isSearchExpanded: boolean;
  getRowId?: (row: TRow) => string | number;
};

export const DataTableBody = <TRow extends RowBase, TActionContext = ActionContext<TRow>>({
  data,
  columns,
  actions,
  bodyClassName,
  rowClassName,
  onRowClick,
  showActionsColumn,
  actionsColumnContent,
  hasActions,
  hasSearchableColumns,
  isSearchExpanded,
  getRowId = defaultGetRowId,
}: Props<TRow, TActionContext>) => {
  const renderRow = (row: TRow, index: number, depth = 0) => {
    const computedRowClassName =
      typeof rowClassName === "function" ? rowClassName(row, index) : rowClassName;

    const rowId = getRowId(row);
    const hasSubRows = row.subRows && row.subRows.length > 0;

    const rowElement = (
      <tr
        key={rowId}
        onClick={() => onRowClick?.(row, index)}
        className={cn(
          "border-gxe-border flex min-w-0 items-center gap-4 border-b py-3 last:border-b-0",
          computedRowClassName,
          {
            "hover:bg-bg-light cursor-pointer": !!onRowClick,
            "bg-gray-50/50": depth > 0,
          }
        )}
        style={{ paddingLeft: depth > 0 ? `${depth * 2.5}rem` : undefined }}
      >
        {columns.map((column) => (
          <td
            key={String(column.key)}
            className={cn("flex min-w-0 text-sm", column.width || "flex-1")}
          >
            {column.render ? (
              column.render(row[column.key], row, index)
            ) : (
              <span className="truncate">{String(row[column.key] ?? "")}</span>
            )}
          </td>
        ))}

        {showActionsColumn && (
          <td className="sticky right-0 z-10 flex min-w-0 flex-shrink-0 items-center justify-end">
            <div className="flex items-center gap-2">
              {actionsColumnContent}
              <div
                className={cn(
                  "flex items-center justify-end transition-all duration-300 ease-in-out",
                  {
                    "w-64": isSearchExpanded,
                    "w-10": !isSearchExpanded && hasSearchableColumns,
                  }
                )}
              >
                {hasActions && actions && (
                  <DropdownMenu
                    className="bg-white pl-5"
                    items={actions.map((action) => ({
                      id: action.id,
                      label: action.label,
                      icon: action.icon,
                      disabled:
                        typeof action.disabled === "function"
                          ? action.disabled({ row, index } as TActionContext)
                          : action.disabled,
                      show:
                        typeof action.show === "function"
                          ? action.show({ row, index } as TActionContext)
                          : action.show,
                      destructive: action.destructive,
                      onClick: () => action.onClick({ row, index } as TActionContext),
                    }))}
                  />
                )}
              </div>
            </div>
          </td>
        )}
      </tr>
    );

    if (hasSubRows) {
      return (
        <Fragment key={rowId}>
          {rowElement}
          {row.subRows!.map((subRow, subIndex) => renderRow(subRow as TRow, subIndex, depth + 1))}
        </Fragment>
      );
    }

    return rowElement;
  };

  return (
    <tbody
      className={cn(
        "border-gxe-border flex flex-col rounded-lg border bg-white px-4",
        bodyClassName
      )}
    >
      {data.map((row, index) => renderRow(row, index, 0))}
    </tbody>
  );
};

DataTableBody.displayName = "DataTableBody";
